/* Las cuentas del cuadro "Clases de uso de suelo y superficie" que la pantalla
 * muestra al pie y en su encabezado.
 *
 * Viven fuera del componente para poder probarlas como funciones puras: dentro
 * del árbol, el único caso que se ejercita con facilidad es "dos números bien
 * escritos", que no distingue una suma en centésimas de una en punto flotante. */

import type {
  CampoDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

import { formatearSuperficie, parsearSuperficie, sumarSuperficies } from "./superficies";

/**
 * Cómo se compara el subtotal del cuadro con la superficie de los roles.
 *
 * RF-E1 §8.2 pide ADVERTIR la diferencia y prohíbe bloquear: esto solo dice qué
 * mostrar, nunca impide guardar.
 */
export type ComparacionConLosRoles =
  | { tipo: "sin_roles" }
  | { tipo: "coincide" }
  | { tipo: "difiere"; roles: number; diferencia: number };

/**
 * Compara el subtotal contra la suma de las superficies de los roles.
 *
 * La suma de los roles va en CENTÉSIMAS (`sumarSuperficies`) y la diferencia se
 * redondea a centésimas: en punto flotante `0,1 + 0,2` no da `0,3` y aparecería
 * una diferencia que no existe. Sin ningún rol con superficie no hay contra qué
 * comparar —un `0` se leería como una medición—.
 */
export function compararConLosRoles(
  subtotal: number,
  superficiesDeLosRoles: (string | null | undefined)[],
): ComparacionConLosRoles {
  const hayAlguna = superficiesDeLosRoles.some((valor) => parsearSuperficie(valor) !== null);
  if (!hayAlguna) return { tipo: "sin_roles" };

  const roles = sumarSuperficies(superficiesDeLosRoles);
  const diferencia = Math.round((subtotal - roles) * 100) / 100;

  return diferencia === 0 ? { tipo: "coincide" } : { tipo: "difiere", roles, diferencia };
}

/** La frase del chip, con las palabras del diseño. */
export function textoDeLaComparacion(
  comparacion: ComparacionConLosRoles,
  unidad: string,
): string {
  switch (comparacion.tipo) {
    case "sin_roles":
      return "Sin superficie de roles para comparar";
    case "coincide":
      return "Coincide con la superficie de los roles";
    case "difiere": {
      const signo = comparacion.diferencia > 0 ? "+" : "";

      return `Roles ${formatearSuperficie(comparacion.roles)} ${unidad} · diferencia ${signo}${formatearSuperficie(comparacion.diferencia)} ${unidad}`;
    }
  }
}

export interface ConteoDeValidadas {
  validadas: number;
  total: number;
}

/** Cuántas filas del cuadro tienen su casilla "Validada" puesta. */
export function contarValidadas(filas: { validada: boolean }[]): ConteoDeValidadas {
  return { validadas: filas.filter((fila) => fila.validada).length, total: filas.length };
}

/** "2 de 3 validadas". */
export function textoDeLasValidadas({ validadas, total }: ConteoDeValidadas): string {
  return `${validadas} de ${total} validadas`;
}

/* --- Lo que el encargado mandó desde terreno --- */

/** Los dos campos de la pestaña que tienen registro de terreno. */
export type CampoDeSuelosEnTerreno = "suelos" | "topografia";

/** La sección "Suelos" del registro de terreno, o `null` sin registro. */
export function seccionDeSuelos(
  registro: RegistroDeTerrenoDelInforme | undefined,
): SeccionDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "suelos") ?? null;
}

/**
 * Lo que el encargado dijo de UNO de los dos textos de la pestaña: la
 * descripción general (`suelos`) o la topografía (`topografia`).
 */
export function campoDeTerrenoDeSuelos(
  registro: RegistroDeTerrenoDelInforme | undefined,
  clave: CampoDeSuelosEnTerreno,
): CampoDeTerreno | null {
  return seccionDeSuelos(registro)?.campos.find((c) => c.clave === clave) ?? null;
}

/* --- Cambios sin guardar --- */

/** Lo que una fila del cuadro tiene de comparable: nunca su clave de edición. */
interface FilaComparable {
  sueloClaseId: number | null;
  descripcion: string;
  superficie: string;
  validada: boolean;
}

function mismaFila(a: FilaComparable | undefined, b: FilaComparable | undefined): boolean {
  return (
    a !== undefined &&
    b !== undefined &&
    a.sueloClaseId === b.sueloClaseId &&
    a.descripcion === b.descripcion &&
    a.superficie === b.superficie &&
    a.validada === b.validada
  );
}

/**
 * Cuántas cosas de la pestaña difieren de lo último guardado: cada campo suelto
 * que cambió cuenta uno y cada fila editada, agregada o quitada cuenta uno.
 *
 * Las filas se comparan por posición y por contenido, nunca por su `clave`: esa
 * no es un dato del informe —cambia al recargar— y encendería el aviso de
 * cambios sin guardar sola. Con `JSON.stringify` sería más corto y más frágil
 * por lo mismo.
 */
export function contarCambios<T extends { filas: FilaComparable[] }>(actual: T, base: T): number {
  const sueltos = (Object.keys(actual) as (keyof T)[]).filter(
    (campo) => campo !== "filas" && actual[campo] !== base[campo],
  ).length;

  const largo = Math.max(actual.filas.length, base.filas.length);
  let filas = 0;

  for (let i = 0; i < largo; i++) {
    if (!mismaFila(actual.filas[i], base.filas[i])) filas++;
  }

  return sueltos + filas;
}
